/**
 * Бюджет автоматических перезагрузок страницы.
 *
 * Проблема: на iOS/WebKit при устаревшем закэшированном index.html, который
 * ссылается на отсутствующий JS-чанк, обработчики ошибок пытаются перезагрузить
 * страницу. Если защита от повтора хранится в dataset/локальной переменной, она
 * сбрасывается при каждой перезагрузке → бесконечный цикл белых перезагрузок.
 *
 * Решение: считаем попытки в URL-параметре `_boot`. Он переживает навигацию и не
 * зависит от localStorage/sessionStorage (которые могут падать в iOS Private Mode).
 * После MAX_AUTO_RELOADS автопопыток перезагрузка прекращается — вызывающий код
 * показывает экран восстановления с ручной кнопкой.
 */

const PARAM = '_boot';
export const MAX_AUTO_RELOADS = 2;

/** Текущее число уже выполненных автоперезагрузок (из URL). */
export function getReloadAttempt() {
  try {
    const value = new URL(window.location.href).searchParams.get(PARAM);
    const n = parseInt(value || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** Есть ли ещё бюджет на автоперезагрузку. */
export function canAutoReload() {
  return getReloadAttempt() < MAX_AUTO_RELOADS;
}

/**
 * Перезагружает страницу с инкрементом счётчика попыток и cache-buster.
 * Возвращает false, если бюджет исчерпан (перезагрузка НЕ выполняется).
 */
export function autoReloadWithBudget() {
  if (!canAutoReload()) return false;
  const next = getReloadAttempt() + 1;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(PARAM, String(next));
    url.searchParams.set('_cb', String(Date.now()));
    url.searchParams.set('_r', Math.random().toString(36).slice(2, 10));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
  return true;
}

/**
 * Убирает технические boot-параметры из URL без перезагрузки.
 * Вызывается после успешного старта приложения, чтобы:
 *   - URL выглядел чисто;
 *   - будущая ручная перезагрузка начинала бюджет заново.
 */
export function clearBootParamsFromUrl() {
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of [PARAM, '_cb', '_r']) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) {
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    /* ignore */
  }
}
